/**
 * Anthropic Claude-based prompt provider
 */

import Anthropic from '@anthropic-ai/sdk';
import { ApiClient } from '../api/client';
import type { 
  PromptProvider, 
  PromptExecuteOptions 
} from '../prompt';
import type { PromptMessage, ScreenshotResponse } from '../types';

interface ToolUseBlock {
  type: 'tool_use';
  id: string;
  name: string;
  input: Record<string, unknown>;
}

interface TextBlock {
  type: 'text';
  text: string;
}

interface ThinkingBlock {
  type: 'thinking';
  thinking: string;
}

interface ImageBlock {
  type: 'image';
  source: {
    type: 'base64';
    media_type: string;
    data: string;
  };
}

type ContentBlock = ToolUseBlock | TextBlock | ThinkingBlock | ImageBlock;

interface ToolResult {
  type: 'tool_result';
  tool_use_id: string;
  content: Array<TextBlock | ImageBlock>;
}

export class AnthropicProvider implements PromptProvider {
  private anthropic: typeof Anthropic;

  constructor() {
    try {
      this.anthropic = Anthropic;
    } catch (error) {
      throw new Error(
        "Anthropic SDK not installed. Please install with 'npm install @anthropic-ai/sdk'"
      );
    }
  }

  public async execute(options: PromptExecuteOptions): Promise<PromptMessage[]> {
    const {
      computerId,
      instruction,
      callback,
      apiKey = process.env.ANTHROPIC_API_KEY,
      model = 'claude-3-7-sonnet-20250219',
      displayWidth = 1024,
      displayHeight = 768,
      orgoApiKey,
      orgoBaseUrl,
      maxSavedScreenshots = 2,
      thinkingEnabled = false,
      thinkingBudget = 1024,
      maxTokens = 4096,
      maxIterations = 20
    } = options;

    if (!apiKey) {
      throw new Error(
        'No Anthropic API key provided. Set ANTHROPIC_API_KEY environment variable or pass apiKey.'
      );
    }

    // Initialize the client
    const client = new this.anthropic({ apiKey });

    // Prepare the messages
    const messages: PromptMessage[] = [
      { role: 'user', content: instruction }
    ];

    // Set up the system prompt
    const systemPrompt = this.getSystemPrompt(displayWidth, displayHeight);

    try {
      // Define the computer tool
      const tools = [
        {
          type: 'computer_20250124',
          name: 'computer',
          display_width_px: displayWidth,
          display_height_px: displayHeight,
          display_number: 1
        }
      ];

      if (callback) {
        callback('status', 'Starting conversation with Claude');
      }

      // Create an API client
      const apiClient = new ApiClient(orgoApiKey, orgoBaseUrl);

      let iteration = 0;
      let screenshotCount = 0;

      // Start the agent loop
      while (iteration < maxIterations) {
        iteration++;

        // Filter screenshots if needed
        if (screenshotCount > maxSavedScreenshots) {
          this.filterToNMostRecentImages(messages, maxSavedScreenshots);
          screenshotCount = maxSavedScreenshots;
        }

        // Create request parameters
        const requestParams: Record<string, unknown> = {
          model,
          max_tokens: maxTokens,
          system: systemPrompt,
          messages: messages as unknown[],
          tools,
          betas: ['computer-use-2025-01-24'],
        };

        // Add thinking parameter only if enabled
        if (thinkingEnabled) {
          requestParams.thinking = {
            type: 'enabled',
            budget_tokens: thinkingBudget
          };
        }

        try {
          const response = await client.beta.messages.create(requestParams as any);
          const responseContent = response.content as ContentBlock[];

          // Add Claude's response to conversation
          const assistantMessage: PromptMessage = {
            role: 'assistant',
            content: responseContent as unknown[]
          };
          messages.push(assistantMessage);

          // Process response blocks
          for (const block of responseContent) {
            if (block.type === 'text' && callback) {
              callback('text', block.text);
            } else if (block.type === 'thinking' && callback) {
              callback('thinking', block.thinking);
            } else if (block.type === 'tool_use' && callback) {
              const toolParams = {
                action: block.name.split('.').pop(),
                ...block.input
              };
              callback('tool_use', toolParams);
            }
          }

          // Execute tool actions
          const toolResults: ToolResult[] = [];
          for (const block of responseContent) {
            if (block.type === 'tool_use') {
              const result = await this.executeTool(
                computerId,
                block.input,
                callback,
                apiClient
              );

              const toolResult: ToolResult = {
                type: 'tool_result',
                tool_use_id: block.id,
                content: []
              };

              // Handle different result types
              if (
                typeof result === 'object' &&
                result !== null &&
                'type' in result &&
                result.type === 'image'
              ) {
                toolResult.content = [result as ImageBlock];
                if (block.input.action === 'screenshot') {
                  screenshotCount++;
                }
              } else {
                toolResult.content = [{
                  type: 'text',
                  text: String(result)
                }];
              }

              toolResults.push(toolResult);
            }
          }

          // If no tools were used, we're done
          if (toolResults.length === 0) {
            if (callback) {
              callback('status', 'Task completed');
            }
            return messages;
          }

          // Add tool results for next iteration
          messages.push({
            role: 'user',
            content: toolResults as unknown[]
          });

        } catch (error) {
          // Handle base64 errors by reducing screenshots
          if (error instanceof Error && error.message.toLowerCase().includes('base64')) {
            if (callback) {
              callback('error', 'Base64 error detected. Attempting recovery...');
            }
            this.filterToNMostRecentImages(messages, 1);
            continue;
          }
          throw error;
        }
      }

      // Max iterations reached
      if (callback) {
        callback('status', `Reached maximum iterations (${maxIterations})`);
      }

      return messages;

    } catch (error) {
      if (callback) {
        callback('error', error instanceof Error ? error.message : String(error));
      }
      throw error;
    }
  }

  private getSystemPrompt(displayWidth: number, displayHeight: number): string {
    return `You are Claude, an AI assistant that controls a virtual Ubuntu computer with internet access.

<SYSTEM_CAPABILITY>
* You are utilising an Ubuntu virtual machine with a display resolution of ${displayWidth}x${displayHeight}.
* You can take screenshots to see the current state and control the computer by clicking, typing, pressing keys, and scrolling.
* The virtual environment is an Ubuntu system with standard applications.
* Always start by taking a screenshot to see the current state before performing any actions.
</SYSTEM_CAPABILITY>

<UBUNTU_DESKTOP_GUIDELINES>
* CRITICAL INSTRUCTION: When opening applications or files on the Ubuntu desktop, you MUST USE DOUBLE-CLICK rather than single-click.
* Single-click only selects desktop icons but DOES NOT open them. To open desktop icons, you MUST use double-click.
* Common desktop interactions:
  - Desktop icons: DOUBLE-CLICK to open applications and folders
  - Menu items: SINGLE-CLICK to select options
  - Taskbar icons: SINGLE-CLICK to open applications
  - Window buttons: SINGLE-CLICK to use close, minimize, maximize buttons
  - File browser items: DOUBLE-CLICK to open folders and files
  - When submitting, use the 'Enter' key, not the 'Return' key.
* If you see an icon on the desktop that you need to open, ALWAYS use the double_click action, never use left_click.
</UBUNTU_DESKTOP_GUIDELINES>

<SCREENSHOT_GUIDELINES>
* Be mindful of how many screenshots you take - they consume significant memory.
* Only take screenshots when you need to see the current state of the screen.
* Try to batch multiple actions before taking another screenshot.
* For better performance, limit the number of screenshots you take.
</SCREENSHOT_GUIDELINES>`;
  }

  private filterToNMostRecentImages(messages: PromptMessage[], maxImages: number): void {
    const imageBlocks: Array<{
      msgIdx: number;
      contentIdx: number;
      contentItemIdx: number;
      contentItem: any;
    }> = [];

    messages.forEach((msg, msgIdx) => {
      if (msg.role !== 'user') return;

      const content = msg.content;
      if (!Array.isArray(content)) return;

      content.forEach((block: any, contentIdx) => {
        if (block?.type !== 'tool_result') return;

        const blockContent = block.content || [];
        blockContent.forEach((item: any, itemIdx: number) => {
          if (item?.type === 'image' && item.source) {
            imageBlocks.push({
              msgIdx,
              contentIdx,
              contentItemIdx: itemIdx,
              contentItem: item
            });
          }
        });
      });
    });

    // Remove oldest images if over limit
    if (imageBlocks.length > maxImages) {
      const toRemove = imageBlocks.slice(0, -maxImages);
      toRemove.forEach(({ contentItem }) => {
        if (contentItem.source?.data) {
          // Replace with a minimal valid base64 image (1x1 transparent PNG)
          contentItem.source.data = 'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==';
          contentItem.source.media_type = 'image/png';
        }
      });
    }
  }

  private async executeTool(
    computerId: string,
    params: Record<string, unknown>,
    callback?: (type: string, data: unknown) => void,
    apiClient?: ApiClient
  ): Promise<string | ImageBlock> {
    const action = params.action as string;

    if (callback) {
      callback('tool_executing', { action, params });
    }

    try {
      if (!apiClient) {
        apiClient = new ApiClient();
      }

      switch (action) {
        case 'screenshot': {
          const response = await apiClient.getScreenshot(computerId) as ScreenshotResponse;
          if (callback) {
            callback('tool_result', { type: 'image', action: 'screenshot' });
          }
          
          // The API now returns a URL instead of base64 data
          // We need to fetch the image from the URL and convert it to base64
          const imageUrl = response.image;
          
          if (!imageUrl) {
            throw new Error('No image URL received from API');
          }
          
          // Fetch the image from the URL
          const imgResponse = await fetch(imageUrl);
          if (!imgResponse.ok) {
            throw new Error(`Failed to fetch image: ${imgResponse.statusText}`);
          }
          
          // Convert to base64
          const arrayBuffer = await imgResponse.arrayBuffer();
          const base64 = Buffer.from(arrayBuffer).toString('base64');
          
          return {
            type: 'image',
            source: {
              type: 'base64',
              media_type: 'image/jpeg',
              data: base64
            }
          };
        }

        case 'left_click': {
          const coord = params.coordinate as number[] | undefined;
          if (!coord) {
            throw new Error('Coordinates required for left click');
          }
          const [x, y] = coord;
          await apiClient.leftClick(computerId, x, y);
          if (callback) {
            callback('tool_result', { action: 'left_click', x, y });
          }
          return `Left-clicked at (${x}, ${y})`;
        }

        case 'right_click': {
          const coord = params.coordinate as number[] | undefined;
          if (!coord) {
            throw new Error('Coordinates required for right click');
          }
          const [x, y] = coord;
          await apiClient.rightClick(computerId, x, y);
          if (callback) {
            callback('tool_result', { action: 'right_click', x, y });
          }
          return `Right-clicked at (${x}, ${y})`;
        }

        case 'double_click': {
          const coord = params.coordinate as number[] | undefined;
          if (!coord) {
            throw new Error('Coordinates required for double click');
          }
          const [x, y] = coord;
          await apiClient.doubleClick(computerId, x, y);
          if (callback) {
            callback('tool_result', { action: 'double_click', x, y });
          }
          return `Double-clicked at (${x}, ${y})`;
        }

        case 'type': {
          const text = params.text as string | undefined;
          if (!text) {
            throw new Error('Text required for typing');
          }
          await apiClient.typeText(computerId, text);
          if (callback) {
            callback('tool_result', { action: 'type', text });
          }
          return `Typed: "${text}"`;
        }

        case 'key': {
          let key = params.text as string | undefined;
          if (!key) {
            throw new Error('Key required for key press');
          }
          // Handle 'return' as 'enter'
          if (key.toLowerCase() === 'return') {
            key = 'enter';
          }
          await apiClient.keyPress(computerId, key);
          if (callback) {
            callback('tool_result', { action: 'key', key });
          }
          return `Pressed key: ${key}`;
        }

        case 'scroll': {
          const direction = params.scroll_direction as string | undefined;
          const amount = params.scroll_amount as number | undefined;
          if (!direction || amount === undefined) {
            throw new Error('Direction and amount required for scrolling');
          }
          await apiClient.scroll(computerId, direction, amount);
          if (callback) {
            callback('tool_result', { action: 'scroll', direction, amount });
          }
          return `Scrolled ${direction} by ${amount}`;
        }

        case 'wait': {
          const duration = (params.duration as number) || 1;
          await apiClient.wait(computerId, duration);
          if (callback) {
            callback('tool_result', { action: 'wait', duration });
          }
          return `Waited for ${duration} second(s)`;
        }

        default: {
          const error = `Unsupported action: ${action}`;
          if (callback) {
            callback('error', error);
          }
          throw new Error(error);
        }
      }
    } catch (error) {
      const errorMsg = `Error executing ${action}: ${error instanceof Error ? error.message : String(error)}`;
      if (callback) {
        callback('error', errorMsg);
      }
      return `Error: ${errorMsg}`;
    }
  }
}